import type { ChangeEvent, JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { getTelegram, haptic } from "../lib/telegram.js";
import { useCurator } from "../useCurator.js";
import { trpc } from "../trpc.js";

const LOCAL_PHOTO_KEY = "companion:self-photo";
const MAX_BYTES = 6 * 1024 * 1024; // ~6 MB, комфортно для base64 + localStorage

/**
 * Каждый юзер обязательно подгружает своё фото — без этого его не увидят
 * другие (и он не увидит портретов собеседниц). В MVP храним в localStorage
 * как base64 (работает без бэка, читается Letter/Match/Curator), параллельно
 * пробуем реальный upload через Telegram-чат: куратор пишет прислать фото
 * в бота, воркер процессит и заполняет photos в БД.
 */
export function AddPhoto(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Регистрирует placeholder-запись photos в DB, чтобы backend знал,
  // что у юзера есть фото → hasBasics=true → onboardingCompleted=true.
  const addPlaceholder = trpc.photos.addPlaceholder.useMutation();

  // Если юзер уже подгружал — подставим превью.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem(LOCAL_PHOTO_KEY);
    if (saved) setDataUrl(saved);
  }, []);

  const onPick = (): void => {
    setError(null);
    inputRef.current?.click();
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError("фото тяжеловато — выберите поменьше");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("нужен файл изображения");
      return;
    }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        setError("не удалось прочитать файл");
        setBusy(false);
        return;
      }
      setDataUrl(result);
      try {
        window.localStorage.setItem(LOCAL_PHOTO_KEY, result);
      } catch {
        // privacy mode — не фейлим
      }
      haptic("success");
      setBusy(false);
    };
    reader.onerror = () => {
      setError("не удалось прочитать файл");
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const onNext = async (): Promise<void> => {
    if (!dataUrl) {
      setError("выберите фото — без него других не увидим");
      return;
    }
    setBusy(true);
    try {
      // Регистрируем placeholder в DB — без этого onboardingCompleted не выставится.
      await addPlaceholder.mutateAsync();
    } catch (err) {
      console.error("photos.addPlaceholder failed", err);
      // Не фейлим онбординг, но даём понять — пользователю не надо блокировать флоу.
    }
    const tg = getTelegram();
    if (tg?.HapticFeedback?.notificationOccurred) {
      tg.HapticFeedback.notificationOccurred("success");
    }
    setBusy(false);
    navigate("/onboarding");
  };

  const onSkip = (): void => {
    // Полный пропуск нельзя (иначе других не увидят) — но если фото уже есть,
    // даём уйти назад в Basics.
    navigate(-1);
  };

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="ваш портрет" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[30px]">
        <div className="editorial-kicker text-accent">{curator.firstName} · просит фото</div>

        <div className="mt-[18px] pr-[18px]">
          <CuratorBubble signature={curator.firstName}>
            «Одно фото — тёплое, честное. По нему я показываю вас тем, кого подбираю. Без портрета
            знакомство не состоится: мы знакомим живых людей, не анкеты.»
          </CuratorBubble>
        </div>

        <h2 className="mt-[28px] font-display font-light leading-[1.02] tracking-[-0.022em] text-ink text-[38px]">
          Портрет
          <br />
          <em className="italic text-velvet">№ I</em>
        </h2>

        <div className="mt-[22px] flex items-center gap-[18px]">
          {/* Рамка-паспарту */}
          <motion.button
            type="button"
            onClick={onPick}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[128px] w-[102px] overflow-hidden border border-ink/18 bg-paper-soft shadow-paper-sm"
            aria-label={dataUrl ? "заменить фото" : "выбрать фото"}
          >
            {dataUrl ? (
              <img
                src={dataUrl}
                alt="ваш портрет"
                className="h-full w-full object-cover"
                style={{ filter: "contrast(1.02) saturate(0.92)" }}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-[6px] px-[10px] text-center">
                <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden>
                  <path
                    d="M12 15a3 3 0 100-6 3 3 0 000 6z M4 8h3l1.5-2h7L17 8h3v11H4z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    className="text-ink/55"
                  />
                </svg>
                <span className="editorial-meta text-ink/45">выбрать фото</span>
              </div>
            )}
          </motion.button>

          <div className="flex-1">
            <p className="font-display italic font-light text-[15px] leading-[1.45] text-ink/75">
              Лицо видно, свет живой, без чёрных очков.
            </p>
            <p className="mt-[6px] font-display italic font-light text-[13px] leading-[1.45] text-ink/50">
              Один снимок — остальные добавим позже.
            </p>
            {dataUrl && (
              <button
                type="button"
                onClick={onPick}
                className="mt-[10px] editorial-meta text-accent hover:opacity-80"
              >
                заменить →
              </button>
            )}
          </div>
        </div>

        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" />

        {error && <div className="mt-[14px] editorial-meta text-velvet">{error}</div>}

        <div className="mt-auto pt-[28px] editorial-meta text-ink/45">
          фото видно только тем, кого куратор вам представит · хранится зашифрованно
        </div>
      </div>

      <div className="px-[22px] pb-[24px] pt-[16px]">
        <PrimaryButton disabled={!dataUrl || busy} onClick={onNext}>
          {busy ? "…" : dataUrl ? "Дальше" : "Выбрать фото"}
        </PrimaryButton>
        <button
          type="button"
          onClick={onSkip}
          className="mt-[10px] block w-full text-center editorial-meta text-ink/45 hover:text-ink/70"
        >
          назад
        </button>
      </div>
    </Screen>
  );
}
